'use server'

import { headers } from 'next/headers'
import { z } from 'zod'

import { auth } from '@/lib/auth'
import { logger } from '@/lib/logger'

export const signIn = async (email: string, password: string) => {
    try {
        const headerList = await headers()
        const ip = headerList.get('x-forwarded-for') || 'unknown'

        const startTime = Date.now()

        const loginScheme = z.object({
            email: z.email(),
            password: z.string().min(6),
        })

        const parseResult = loginScheme.safeParse({ email, password })
        if (!parseResult.success) {
            logger.info(`AUTH_FAILED: User ${email} from IP: ${ip}`)
            return { needTwoFactor: false, success: false, message: 'Invalid input!', msgType: 'error', url: '/' }
        }

        console.log(`Attempting to sign in user: ${email} took ${Date.now() - startTime}ms`)

        const response = await auth.api.signInEmail({
            body: {
                email,
                password,
                callbackURL: '/home',
            },
        })

        const hasTwoFactor = 'twoFactorRedirect' in response && response.twoFactorRedirect

        if (hasTwoFactor) {
            console.log(`User ${email} requires Two-Factor Authentication, initiating flow took ${Date.now() - startTime}ms`)
            return { needTwoFactor: true, success: false, message: 'Two-Factor Authentication required', msgType: 'info', url: '/' }
        }

        let { user, token } = response

        console.log(`Sign-in response for user: ${email} took ${Date.now() - startTime}ms`, { user, token })

        return await handlePostLogin({ user, token })
    } catch (error) {
        const headerList = await headers()
        const ip = headerList.get('x-forwarded-for') || 'unknown'

        const e = error as Error
        console.log('Error during sign-in:', e.message)

        if (e.message === 'Invalid email or password') {
            logger.info(`AUTH_FAILED: User ${email} from IP: ${ip}`)
        }

        return { needTwoFactor: false, success: false, message: e.message || 'An error occurred during sign-in.', msgType: 'error', url: '/' }
    }
}

async function handlePostLogin({ user, token }: { user: any; token: string }) {
    if (!user || !token) {
        //logger.info(`AUTH_FAILED: User ${email} from IP: ${ip}`)
        return { needTwoFactor: false, success: false, message: 'Invalid credentials!', msgType: 'error', url: '/' }
    }

    console.log(`User signed in successfully: ${user.email}`)

    const data = await auth.api.userHasPermission({
        body: {
            userId: user.id,
            permission: { dashboard: ['login'] },
        },
    })

    console.log(`Permission check result for user: ${user.email}`)

    if (data && !data.success) {
        logger.info(`PERMISSION_DENIED: User ${user.email}`)

        const { success } = await auth.api.signOut({
            headers: await headers(),
        })

        if (!success) {
            throw new Error('Sign-out failed after permission denial')
        }

        return { needTwoFactor: false, success: false, message: 'Permission denied!', msgType: 'error', url: '/' }
    } else {
        return { needTwoFactor: false, success: true, message: 'Sign-in successful', msgType: 'success', url: '/home' }
    }
}

export async function sendTwoFactorCode(code: string) {
    try {
        const { user, token } = await auth.api.verifyTOTP({
            body: {
                code,
                trustDevice: true,
            },
            headers: await headers(),
        })

        return await handlePostLogin({ user, token })
    } catch (error) {
        const e = error as Error
        console.log('Error during Two-Factor verification:', e.message)

        return { success: false, message: e.message || 'An error occurred during Two-Factor verification.', url: '/' }
    }
}

export async function signUp() {
    await auth.api.signUpEmail({
        body: {
            email: 'norbert-stark@gmx.net',
            password: 'dpbN1234!',
            name: 'Norbert Stark',
        },
    })

    return
}
